import React, { CSSProperties, FC } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import SwiperType from 'swiper';
import { useSwipeNavigation } from './hooks/useSwipeNavigation';

export type SliderNavigationProps = {
  compactNavigation?: boolean;
  arrowIconNavigation?: boolean;
  arrows?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  arrowButtonClasses?: string;
  prevButtonStyles?: CSSProperties;
  nextButtonStyles?: CSSProperties;
  prevArrowStyles?: CSSProperties;
  nextArrowStyles?: CSSProperties;
  totalSlides?: number;
  swiperRef?: SwiperType;
  isLoopMode?: boolean;
};

const ARROW_STYLES = {
  compact: {
    className: 'h-20 w-20 hover:cursor-pointer',
    strokeWidth: 1,
    color: 'var(--color-neutral-black-400-v)',
  },
  icon: {
    className: 'h-24 w-24 hover:cursor-pointer',
    strokeWidth: 1,
    color: 'var(--color-neutral-black-v)',
  },
} as const;

const SliderNavigation: FC<SliderNavigationProps> = ({
  compactNavigation,
  arrowIconNavigation,
  arrows,
  innerArrows,
  solidArrows,
  arrowButtonClasses,
  prevButtonStyles,
  nextButtonStyles,
  prevArrowStyles,
  nextArrowStyles,
  totalSlides,
  swiperRef,
  isLoopMode,
}) => {
  const { showPrevArrow, showNextArrow } = useSwipeNavigation({ swiperRef, totalSlides, isLoopMode });

  if (compactNavigation) {
    if (!arrows) return null;
    return (
      <div className="mt-10 flex justify-center gap-16">
        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => swiperRef?.slidePrev()}
          className="border-none bg-transparent p-0"
        >
          <ChevronLeftIcon {...ARROW_STYLES.compact} />
        </button>
        <div className="flex font-body text-14 font-regular leading-loose text-secondary-black">
          <span>{((swiperRef?.activeIndex ?? 0) % (totalSlides ?? 1)) + 1}</span>
          <span>/</span>
          <span>{totalSlides}</span>
        </div>
        <button
          type="button"
          aria-label="Next slide"
          onClick={() => swiperRef?.slideNext()}
          className="border-none bg-transparent p-0"
        >
          <ChevronRightIcon {...ARROW_STYLES.compact} />
        </button>
      </div>
    );
  }

  const arrowClassNames = {
    base: 'slider_arrow',
    prev: 'slider_arrow_prev',
    next: 'slider_arrow_next',
    inner: innerArrows ? 'slider_arrow_inner' : '',
    solid: solidArrows ? 'slider_arrow_solid' : '',
  };

  const iconStyles = {
    transform: 'translateY(-20px)',
  };

  if (!arrows) return null;

  const handlePrevClick = () => {
    swiperRef?.slidePrev();
  };

  const handleNextClick = () => {
    swiperRef?.slideNext();
  };

  return (
    <div>
      {showPrevArrow && (
        <button
          type="button"
          aria-label="Previous slide"
          className={`border-none bg-transparent p-0 ${arrowClassNames.base} ${arrowClassNames.prev} ${arrowClassNames.inner} ${arrowClassNames.solid} ${arrowButtonClasses}`}
          style={{ ...prevButtonStyles }}
          onClick={handlePrevClick}
        >
          {arrowIconNavigation ? (
            <ArrowLeftIcon {...ARROW_STYLES.icon} style={iconStyles} />
          ) : (
            <span className="arrow" style={prevArrowStyles} />
          )}
        </button>
      )}

      {showNextArrow && (
        <button
          type="button"
          aria-label="Next slide"
          className={`border-none bg-transparent p-0 ${arrowClassNames.base} ${arrowClassNames.next} ${arrowClassNames.inner} ${arrowClassNames.solid} ${arrowButtonClasses}`}
          style={{ ...nextButtonStyles }}
          onClick={handleNextClick}
        >
          {arrowIconNavigation ? (
            <ArrowRightIcon {...ARROW_STYLES.icon} style={iconStyles} />
          ) : (
            <span className="arrow" style={nextArrowStyles} />
          )}
        </button>
      )}
    </div>
  );
};

export default SliderNavigation;
